import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { ListingService } from "@/services/listing/listingService";
import {
  ListingQueryParams,
  ListingBiddingParticipation,
  GetAllListingsResponse,
} from "@/types/listing";
import type { Query } from "@tanstack/react-query";
// TODO: Fix an endpoint to fetch some random listings? Or perhaps popular ones? Exclude the current listing. Only include active listings!. Will be used by OtherListingsCarousel.tsx

type ListingsRefetchInterval = (
  query: Query<GetAllListingsResponse, Error>,
) => number | false | undefined;

/** För dig: small pages + seeded random (no duplicates across pages). */
export const FOR_YOU_PAGE_SIZE = 12;
export const FOR_YOU_SORT = "random";
export const FOR_YOU_SEED_STORAGE_KEY = "forYou_feedSeed";

/** Session-stable seed so infinite pages stay a single shuffle order. */
export function getOrCreateForYouSeed(): string {
  try {
    const existing = sessionStorage.getItem(FOR_YOU_SEED_STORAGE_KEY);
    if (existing) return existing;
    const seed = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(FOR_YOU_SEED_STORAGE_KEY, seed);
    return seed;
  } catch {
    return `fy_${Date.now().toString(36)}`;
  }
}

/** Rotate seed (e.g. pull-to-refresh wants a fresh shuffle). */
export function rotateForYouSeed(): string {
  try {
    sessionStorage.removeItem(FOR_YOU_SEED_STORAGE_KEY);
  } catch {
    // ignore
  }
  return getOrCreateForYouSeed();
}

export const useListing = () => {
  const queryClient = useQueryClient();

  // Get all listings query (lazy - only runs when explicitly used)
  const getAllListingsQuery = useQuery({
    queryKey: ["listings"],
    queryFn: () => ListingService.getAllListings(),
    enabled: false, // Don't run automatically - only when explicitly requested
    staleTime: 0 * 60 * 1000, // 5 minutes
    gcTime: 0 * 60 * 1000, // No cache retention
  });

  // Get listings with filters
  const getListingsWithFilters = (
    params: ListingQueryParams,
    options?: {
      enabled?: boolean;
      refetchInterval?: number | false | ListingsRefetchInterval;
      refetchIntervalInBackground?: boolean;
    },
  ) => {
    return useQuery({
      queryKey: ["listings", params],
      queryFn: () => ListingService.getAllListings(params),
      enabled: options?.enabled !== false,
      refetchInterval: options?.refetchInterval,
      refetchIntervalInBackground: options?.refetchIntervalInBackground ?? false,
      //staleTime: 5 * 60 * 1000,
    });
  };

  /** Infinite För dig feed — seeded random pages (no re-shows within the seed). */
  const getForYouListingsInfinite = (
    params: Omit<ListingQueryParams, "page" | "limit" | "sort"> = {},
    options?: { enabled?: boolean },
  ) => {
    return useInfiniteQuery({
      queryKey: ["listings", "for-you", params],
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        ListingService.getAllListings({
          ...params,
          page: pageParam,
          limit: FOR_YOU_PAGE_SIZE,
          sort: FOR_YOU_SORT,
          seed: params.seed || getOrCreateForYouSeed(),
        }),
      getNextPageParam: (lastPage) => {
        const page = lastPage.page ?? 1;
        const totalPages = lastPage.totalPages ?? 1;
        return page < totalPages ? page + 1 : undefined;
      },
      enabled: options?.enabled !== false,
    });
  };

  const getPublicForYouListingsInfinite = (
    params: Omit<ListingQueryParams, "page" | "limit" | "sort"> = {},
    options?: { enabled?: boolean },
  ) => {
    return useInfiniteQuery({
      queryKey: ["listings", "public", "for-you", params],
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        ListingService.getPublicListings({
          ...params,
          page: pageParam,
          limit: FOR_YOU_PAGE_SIZE,
          sort: FOR_YOU_SORT,
          seed: params.seed || getOrCreateForYouSeed(),
        }),
      getNextPageParam: (lastPage) => {
        const page = lastPage.page ?? 1;
        const totalPages =
          lastPage.totalPages ||
          Math.ceil((lastPage.totalCount || 0) / FOR_YOU_PAGE_SIZE) ||
          1;
        return page < totalPages ? page + 1 : undefined;
      },
      enabled: options?.enabled !== false,
    });
  };

  // Get my listings (seller listings with bidding data)
  const getMyListings = (
    params: Omit<ListingQueryParams, "seller"> = {},
    options?: { enabled?: boolean },
  ) => {
    return useQuery({
      queryKey: ["listings", "my-listings", params],
      queryFn: () => ListingService.getMyListings(params),
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
      enabled: options?.enabled !== false,
    });
  };

  // Get my orders (seller listings with payment and transport data)
  const getMyOrders = (params: Omit<ListingQueryParams, "seller"> = {}) => {
    return useQuery({
      queryKey: ["listings", "my-orders", params],
      queryFn: () => ListingService.getMyOrders(params),
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
    });
  };

  // Get my biddings participation (auctions where user has bid)
  const getMyBiddingsParticipation = (
    params: Omit<ListingQueryParams, "seller" | "winner"> = {},
    options?: { enabled?: boolean },
  ) => {
    return useQuery({
      queryKey: ["listings", "my-biddings-participation", params],
      queryFn: () => ListingService.getMyBiddingsParticipation(params),
      enabled: options?.enabled !== false,
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
    });
  };

  // Reserve listing mutation (Buy Now flow)
  const reserveListingMutation = useMutation({
    mutationFn: ({ listingId }: { listingId: string }) => {
      return ListingService.reserveListing(listingId);
    },
    onSuccess: (data, variables) => {
      // Invalidate listing queries to refetch with new status
      queryClient.invalidateQueries({
        queryKey: ["listing", variables.listingId],
      });
      queryClient.invalidateQueries({ queryKey: ["listings"] });
    },
    onError: (error) => {},
  });

  const submitListingInquiryMutation = useMutation({
    mutationFn: ({
      listingId,
      message,
    }: {
      listingId: string;
      message: string;
    }) => ListingService.submitListingInquiry(listingId, message),
  });

  const setAutomaticBidMutation = useMutation({
    mutationFn: ({
      listingId,
      maxAmount,
    }: {
      listingId: string;
      maxAmount: number;
    }) => ListingService.setAutomaticBid(listingId, maxAmount),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["listing", variables.listingId],
      });
    },
  });

  const cancelAutomaticBidMutation = useMutation({
    mutationFn: ({ listingId }: { listingId: string }) =>
      ListingService.cancelAutomaticBid(listingId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["listing", variables.listingId],
      });
    },
  });

  // Remove auction from my biddings participation mutation
  const removeAuctionFromMyBiddingsMutation = useMutation({
    mutationFn: ({ listingId }: { listingId: string }) =>
      ListingService.removeAuctionFromMyBiddingsParticipation(listingId),
    onMutate: async ({ listingId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["listings", "my-biddings-participation"],
      });

      // Get all query data (could be multiple pages)
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: ["listings", "my-biddings-participation"],
      });

      // Snapshot all previous values
      const previousDataMap = new Map();
      queries.forEach((query) => {
        previousDataMap.set(query.queryKey, query.state.data);
      });

      // Optimistically update all queries - remove the listing
      queries.forEach((query) => {
        const data = query.state.data as
          | {
              listings: ListingBiddingParticipation[];
              totalCount: number;
              totalPages: number;
              page: number;
              limit: number;
            }
          | undefined;

        if (data) {
          queryClient.setQueryData(query.queryKey, {
            ...data,
            listings: data.listings.filter(
              (listing) => listing.id !== listingId,
            ),
            totalCount: Math.max(0, data.totalCount - 1),
          });
        }
      });

      return { previousDataMap };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousDataMap) {
        context.previousDataMap.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({
        queryKey: ["listings", "my-biddings-participation"],
      });
    },
  });

  // Get specific listing (for CarDetails)
  const getListing = (listingId: string, userId?: string) => {
    return useQuery({
      queryKey: ["listing", listingId, userId],
      queryFn: () => ListingService.getListing(listingId, userId),
      enabled: !!listingId,
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
      refetchOnWindowFocus: true,
    });
  };

  const getPublicListing = (listingId: string) => {
    return useQuery({
      queryKey: ["listing", "public", listingId],
      queryFn: () => ListingService.getPublicListing(listingId),
      enabled: !!listingId,
      staleTime: 5 * 60 * 1000,
    });
  };

  const getPublicListingsWithFilters = (
    params: ListingQueryParams,
    options?: {
      enabled?: boolean;
      refetchInterval?: number | false | ListingsRefetchInterval;
      refetchIntervalInBackground?: boolean;
    },
  ) => {
    return useQuery({
      queryKey: ["listings", "public", params],
      queryFn: () => ListingService.getPublicListings(params),
      enabled: options?.enabled !== false,
      refetchInterval: options?.refetchInterval,
      refetchIntervalInBackground: options?.refetchIntervalInBackground ?? false,
    });
  };

  // Fetch rating intervals for a listing (for CarDetails — POST, same path as vehicle-card fetch server-side)
  const getListingRatingIntervals = (
    listingId: string,
    options?: { enabled?: boolean },
  ) => {
    return useQuery({
      queryKey: ["listing", listingId, "rating-intervals", "fetch"],
      queryFn: () => ListingService.fetchListingRatingIntervals(listingId),
      enabled: options?.enabled !== false && !!listingId,
      staleTime: 10 * 60 * 1000, // 10 min cache
    });
  };

  // Get public listings for carousel (unprotected endpoint)
  const getPublicListingsCarousel = (limit: number = 10) => {
    return useQuery({
      queryKey: ["listings", "public", "carousel", limit],
      queryFn: () => ListingService.getPublicListingsCarousel(limit),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get listing bids
  const getListingBids = (
    listingId: string,
    options?: { enabled?: boolean },
  ) => {
    return useQuery({
      queryKey: ["listing", listingId, "bids"],
      queryFn: () => ListingService.getListingBids(listingId),
      enabled: options?.enabled !== false && !!listingId,
      staleTime: 0 * 60 * 1000, // Always fresh
    });
  };

  return {
    // Queries
    getAllListings: getAllListingsQuery.data,
    isLoadingListings: getAllListingsQuery.isLoading,
    listingsError: getAllListingsQuery.error,
    refetchAllListings: getAllListingsQuery.refetch, // Expose refetch for manual triggering

    // Query functions
    getListingsWithFilters,
    getForYouListingsInfinite,
    getPublicForYouListingsInfinite,
    getMyListings,
    getMyOrders,
    getMyBiddingsParticipation,
    getListing,
    getPublicListing,
    getPublicListingsWithFilters,
    getListingRatingIntervals,
    getPublicListingsCarousel,
    getListingBids,

    // Mutations
    removeAuctionFromMyBiddings: removeAuctionFromMyBiddingsMutation.mutate,
    removeAuctionFromMyBiddingsAsync:
      removeAuctionFromMyBiddingsMutation.mutateAsync,
    isRemovingAuctionFromMyBiddings:
      removeAuctionFromMyBiddingsMutation.isPending,
    removeAuctionFromMyBiddingsError: removeAuctionFromMyBiddingsMutation.error,
    removeAuctionFromMyBiddingsSuccess:
      removeAuctionFromMyBiddingsMutation.isSuccess,

    // Reserve listing mutations
    reserveListing: reserveListingMutation.mutate,
    reserveListingAsync: reserveListingMutation.mutateAsync,
    isReservingListing: reserveListingMutation.isPending,
    reserveListingError: reserveListingMutation.error,
    reserveListingSuccess: reserveListingMutation.isSuccess,

    submitListingInquiryAsync: submitListingInquiryMutation.mutateAsync,
    isSubmittingListingInquiry: submitListingInquiryMutation.isPending,
    submitListingInquiryError: submitListingInquiryMutation.error,
    resetSubmitListingInquiry: submitListingInquiryMutation.reset,

    setAutomaticBidAsync: setAutomaticBidMutation.mutateAsync,
    isSettingAutomaticBid: setAutomaticBidMutation.isPending,
    cancelAutomaticBidAsync: cancelAutomaticBidMutation.mutateAsync,
    isCancellingAutomaticBid: cancelAutomaticBidMutation.isPending,
    isAutomaticBidMutating:
      setAutomaticBidMutation.isPending ||
      cancelAutomaticBidMutation.isPending,

    // Reset functions
    resetRemoveAuctionFromMyBiddings: removeAuctionFromMyBiddingsMutation.reset,
    resetReserveListing: reserveListingMutation.reset,
  };
};
